/**
 * RolePanel — full inline editor for a Role definition. Used inside the
 * SlidePanel on the Roles page. Fields:
 *   - Label (inline text)
 *   - Description (inline textarea)
 *   - Capabilities list (add / remove with tool, label, access)
 */
import { useState } from 'react';
import {
  X, Pencil, Check, Loader2, Plus, Trash2, Users as UsersIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useUpdateRoleDefinition } from '../api/roleDefinitions';
import type { RoleDefinition, RoleCapability } from '../types';

interface Props {
  role: RoleDefinition;
  onClose: () => void;
}

function InlineText({
  value, onSave, multiline = false, placeholder = 'Click to edit',
}: {
  value: string | null;
  onSave: (v: string) => Promise<void>;
  multiline?: boolean;
  placeholder?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  const [saving, setSaving] = useState(false);

  const commit = async () => {
    setSaving(true);
    try { await onSave(draft.trim()); setEditing(false); } finally { setSaving(false); }
  };

  if (editing) {
    return (
      <div className="space-y-2">
        {multiline ? (
          <Textarea rows={5} value={draft} onChange={(e) => setDraft(e.target.value)} autoFocus />
        ) : (
          <Input value={draft} onChange={(e) => setDraft(e.target.value)} autoFocus />
        )}
        <div className="flex gap-2">
          <Button size="sm" onClick={commit} disabled={saving}>
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => { setDraft(value ?? ''); setEditing(false); }}>
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      onClick={() => setEditing(true)}
      className="cursor-pointer hover:bg-muted/50 rounded px-2 py-1 -mx-2 -my-1 group relative min-h-[2rem]"
    >
      {value ? (
        <span className={multiline ? 'whitespace-pre-wrap text-sm' : ''}>{value}</span>
      ) : (
        <span className="text-muted-foreground italic">{placeholder}</span>
      )}
      <Pencil className="absolute top-1 right-1 h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100" />
    </div>
  );
}

export function RolePanel({ role, onClose }: Props) {
  const update = useUpdateRoleDefinition();

  const saveLabel = async (label: string) => {
    if (!label) return;
    await update.mutateAsync({ role: role.role, patch: { label } });
  };
  const saveDescription = async (description: string) => {
    await update.mutateAsync({ role: role.role, patch: { description } });
  };
  const saveCapabilities = (capabilities: RoleCapability[]) =>
    update.mutateAsync({ role: role.role, patch: { capabilities } });

  // Add-capability form
  const [adding, setAdding] = useState(false);
  const [draftTool, setDraftTool] = useState('');
  const [draftLabel, setDraftLabel] = useState('');
  const [draftAccess, setDraftAccess] = useState<RoleCapability['access']>('read');

  const addCapability = async () => {
    if (!draftTool.trim() || !draftLabel.trim()) return;
    await saveCapabilities([
      ...role.capabilities,
      { tool: draftTool.trim(), label: draftLabel.trim(), access: draftAccess },
    ]);
    setDraftTool(''); setDraftLabel(''); setDraftAccess('read');
    setAdding(false);
  };

  const removeCapability = (idx: number) =>
    saveCapabilities(role.capabilities.filter((_, i) => i !== idx));

  const toggleAccess = async (idx: number) => {
    const next = role.capabilities.map((c, i) =>
      i === idx ? { ...c, access: c.access === 'read' ? 'read-write' : 'read' } as RoleCapability : c,
    );
    await saveCapabilities(next);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Sticky header */}
      <div className="sticky top-0 z-10 bg-background border-b px-4 py-3 flex items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-semibold truncate">{role.label}</h2>
          <div className="text-[11px] font-mono text-muted-foreground truncate">{role.role}</div>
        </div>
        <Badge variant="secondary" className="shrink-0">
          <UsersIcon className="h-3 w-3 mr-1" /> {role.member_count ?? 0}
        </Badge>
        <Button type="button" variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-5">
        <section className="space-y-2">
          <Label className="text-xs">Display label</Label>
          <InlineText value={role.label} onSave={saveLabel} placeholder="e.g. Sales Manager" />
        </section>

        <section className="space-y-2 border-t pt-4">
          <Label className="text-xs">Description</Label>
          <InlineText
            value={role.description}
            onSave={saveDescription}
            multiline
            placeholder="What this role is responsible for…"
          />
        </section>

        <section className="space-y-2 border-t pt-4">
          <div className="flex items-center justify-between">
            <Label className="text-xs">Capabilities ({role.capabilities.length})</Label>
            {!adding && (
              <Button size="sm" variant="outline" className="h-7" onClick={() => setAdding(true)}>
                <Plus className="h-3.5 w-3.5 mr-1" /> Add
              </Button>
            )}
          </div>

          {adding && (
            <div className="border rounded-md p-2 space-y-2 bg-muted/30">
              <Input
                className="h-8 text-sm"
                placeholder="Display label (e.g. Edit invoices)"
                value={draftLabel}
                onChange={(e) => setDraftLabel(e.target.value)}
                autoFocus
              />
              <Input
                className="h-8 text-sm font-mono text-xs"
                placeholder="Tool key (e.g. invoices.modify)"
                value={draftTool}
                onChange={(e) => setDraftTool(e.target.value)}
              />
              <div className="flex gap-2">
                <Select value={draftAccess} onValueChange={(v) => setDraftAccess(v as RoleCapability['access'])}>
                  <SelectTrigger className="h-8 text-sm w-32"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="read">read</SelectItem>
                    <SelectItem value="read-write">read-write</SelectItem>
                  </SelectContent>
                </Select>
                <Button size="sm" onClick={addCapability} disabled={!draftLabel.trim() || !draftTool.trim() || update.isPending}>
                  {update.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Add'}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => { setAdding(false); setDraftLabel(''); setDraftTool(''); setDraftAccess('read'); }}>
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {role.capabilities.length === 0 && !adding ? (
            <div className="text-xs text-muted-foreground italic py-2">No capabilities documented.</div>
          ) : (
            <div className="space-y-1">
              {role.capabilities.map((c, idx) => (
                <div key={idx} className="flex items-center gap-2 group hover:bg-muted/40 rounded px-2 py-1.5 border">
                  <button
                    type="button"
                    onClick={() => toggleAccess(idx)}
                    title="Toggle access"
                  >
                    <Badge variant="secondary" className={cn(
                      'text-[10px] cursor-pointer hover:opacity-80',
                      c.access === 'read-write' ? 'bg-emerald-100 text-emerald-800' : 'bg-blue-100 text-blue-800',
                    )}>
                      {c.access}
                    </Badge>
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{c.label}</div>
                    <div className="text-[11px] font-mono text-muted-foreground truncate">{c.tool}</div>
                  </div>
                  <Button
                    size="icon" variant="ghost"
                    className="h-7 w-7 opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive"
                    onClick={() => removeCapability(idx)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      {/* Sticky footer */}
      <div className="sticky bottom-0 bg-background border-t px-4 py-2 flex items-center justify-end">
        <Button type="button" variant="outline" size="sm" onClick={onClose}>Close</Button>
      </div>
    </div>
  );
}

export default RolePanel;
